import { cn } from '@amore/ui/cn'
import {
  Toast,
  ToastAction,
  ToastClose,
  ToastContent,
  ToastDescription,
  ToastIcon,
  ToastProvider,
  ToastTitle,
  type ToastTone,
  ToastViewport,
} from '@amore/ui/components/toast'
import {
  BellIcon,
  CircleAlertIcon,
  CircleCheckIcon,
  InfoIcon,
  TriangleAlertIcon,
} from 'lucide-react'
import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from 'react'

type ToastActionOptions = {
  label: string
  /** What the action does, for screen readers that reach the toast later. */
  altText: string
  onClick: () => void
}

/**
 * One notification: a short title (what happened), an optional one-line
 * description (which record), and at most two small inline buttons.
 * `action` is the outline button (undo, open); `primaryAction` the solid
 * one, for the single follow-up that moves the user on.
 */
export type ToastOptions = {
  title: string
  description?: string
  tone?: ToastTone
  duration?: number
  action?: ToastActionOptions
  primaryAction?: ToastActionOptions
}

type ToastEntry = ToastOptions & { id: number }
type ToastContextValue = {
  showToast: (options: ToastOptions) => number
  dismissToast: (id: number) => void
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined)
let nextToastId = 0

const toneIcons = {
  neutral: BellIcon,
  info: InfoIcon,
  success: CircleCheckIcon,
  warning: TriangleAlertIcon,
  danger: CircleAlertIcon,
} satisfies Record<ToastTone, typeof BellIcon>

const toneIconStyles: Record<ToastTone, string> = {
  neutral: 'bg-muted text-muted-foreground',
  info: 'bg-info text-info-foreground',
  success: 'bg-success text-success-foreground',
  warning: 'bg-warning text-warning-foreground',
  danger: 'bg-danger text-danger-foreground',
}

const actionButton =
  'm-0 min-h-8 self-auto rounded-full border px-3.5 py-1 text-xs font-semibold transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring'
const outlineButton = 'border-border bg-card text-foreground hover:bg-muted'
const solidButton = 'border-transparent bg-foreground text-background hover:bg-foreground/85'

export function ToasterProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastEntry[]>([])

  const dismissToast = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id))
  }, [])

  const showToast = useCallback((options: ToastOptions) => {
    const id = ++nextToastId
    setToasts((current) => [...current, { ...options, id }].slice(-4))
    return id
  }, [])

  const value = useMemo(() => ({ dismissToast, showToast }), [dismissToast, showToast])

  return (
    <ToastContext value={value}>
      <ToastProvider label="Oznámení" duration={6_000} swipeDirection="right">
        {children}
        {toasts.map((toast) => {
          const tone = toast.tone ?? 'neutral'
          const Icon = toneIcons[tone]

          const hasActions = toast.action || toast.primaryAction

          return (
            <Toast
              key={toast.id}
              open
              tone={tone}
              duration={toast.duration ?? (hasActions ? 8_000 : undefined)}
              onOpenChange={(open) => {
                if (!open) dismissToast(toast.id)
              }}
              className="items-start gap-2 rounded-panel border border-l border-border border-l-border p-3 pr-10 data-[state=open]:slide-in-from-bottom-2 data-[state=open]:slide-in-from-right-0"
            >
              <ToastIcon
                className={cn(
                  'mt-0.5 grid size-5 place-items-center rounded-full [&>svg]:size-3.5',
                  toneIconStyles[tone],
                )}
              >
                <Icon aria-hidden="true" />
              </ToastIcon>
              <ToastContent className="flex flex-wrap items-center gap-x-3 gap-y-2">
                <div className="min-w-0 flex-1 basis-36">
                  <ToastTitle>{toast.title}</ToastTitle>
                  {toast.description ? (
                    <ToastDescription className="mt-0.5 break-words text-xs leading-4">
                      {toast.description}
                    </ToastDescription>
                  ) : null}
                </div>
                {hasActions ? (
                  <div className="flex flex-wrap items-center gap-1.5">
                    {toast.action ? (
                      <ToastAction
                        altText={toast.action.altText}
                        onClick={toast.action.onClick}
                        className={cn(actionButton, outlineButton)}
                      >
                        {toast.action.label}
                      </ToastAction>
                    ) : null}
                    {toast.primaryAction ? (
                      <ToastAction
                        altText={toast.primaryAction.altText}
                        onClick={toast.primaryAction.onClick}
                        className={cn(actionButton, solidButton)}
                      >
                        {toast.primaryAction.label}
                      </ToastAction>
                    ) : null}
                  </div>
                ) : null}
              </ToastContent>
              <ToastClose
                label="Zavřít oznámení"
                className="top-1.5 right-1 size-8 [&>svg]:size-3.5"
              />
            </Toast>
          )
        })}
        <ToastViewport
          label="Oznámení ({hotkey})"
          className="right-auto left-1/2 w-full -translate-x-1/2 gap-2 px-4 pt-0 sm:w-[28rem] sm:px-0 sm:pt-0"
        />
      </ToastProvider>
    </ToastContext>
  )
}

export function useToast() {
  const context = useContext(ToastContext)
  if (!context) throw new Error('useToast must be used within ToasterProvider')
  return context
}
